import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getCurrencies = createAsyncThunk(
  "currencies/getCurrencies",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().currencies;
      if (state.count.getCurrencies <= 1) {
        const res = await axios.get("/currencies", { params: reqData });
        return res.data;
      }
      return { count: state.total, data: state.data };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getCurrency = createAsyncThunk(
  "currencies/getCurrency",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.get("/currency/" + reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const addCurrency = createAsyncThunk(
  "currencies/addCurrency",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-currency", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editCurrency = createAsyncThunk(
  "currencies/editCurrency",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-currency/" + id, data);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteCurrency = createAsyncThunk(
  "currencies/deleteCurrency",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-currency/" + id);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const currenciesSlice = createSlice({
  name: "currencies",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getCurrencies: false,
      getCurrency: false,
      addCurrency: false,
      editCurrency: false,
      deleteCurrency: false,
    },
    count: { getCurrencies: 0, getCurrency: 0 },
  },
  reducers: {
    setCurrencies: (state, action) => {
      state.data = action.payload;
    },
    setSelectedCurrency: (state, action) => {
      state.selected = action.payload;
    },
    unsetCurrencies: (state) => {
      state.data = [];
      state.selected = {};
    },
    unsetSelectedCurrency: (state) => {
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Currencies
      .addCase(getCurrencies.pending, (state) => {
        state.load.getCurrencies = loadStatus.loading;
        state.error = null;
        state.count.getCurrencies = ++state.count.getCurrencies;
      })
      .addCase(getCurrencies.fulfilled, (state, action) => {
        state.load.getCurrencies = loadStatus.success;
        state.total = action.payload.count;
        state.data = action.payload.data;
        state.count.getCurrencies = 0;
      })
      .addCase(getCurrencies.rejected, (state, action) => {
        state.load.getCurrencies = loadStatus.error;
        state.error = action.payload;
        state.count.getCurrencies = 0;
      })
      //Get Currency
      .addCase(getCurrency.pending, (state) => {
        state.load.getCurrency = loadStatus.loading;
        state.error = null;
      })
      .addCase(getCurrency.fulfilled, (state, action) => {
        state.load.getCurrency = loadStatus.success;
        state.selected = action.payload;
      })
      .addCase(getCurrency.rejected, (state, action) => {
        state.load.getCurrency = loadStatus.error;
        state.error = action.payload;
      })
      //Add Currency
      .addCase(addCurrency.pending, (state) => {
        state.load.addCurrency = loadStatus.loading;
        state.error = null;
      })
      .addCase(addCurrency.fulfilled, (state, action) => {
        state.load.addCurrency = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addCurrency.rejected, (state, action) => {
        state.load.addCurrency = loadStatus.error;
        state.error = action.payload;
      })
      //Update Currency
      .addCase(editCurrency.pending, (state) => {
        state.load.editCurrency = loadStatus.loading;
        state.error = null;
      })
      .addCase(editCurrency.fulfilled, (state, action) => {
        state.load.editCurrency = loadStatus.success;
        state.selected = action.payload;
        const index = state.data.findIndex(
          (item) => (item.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editCurrency.rejected, (state, action) => {
        state.load.editCurrency = loadStatus.error;
        state.error = action.payload;
      })
      //Delete Currency
      .addCase(deleteCurrency.pending, (state) => {
        state.load.deleteCurrency = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteCurrency.fulfilled, (state, action) => {
        state.load.deleteCurrency = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteCurrency.rejected, (state, action) => {
        state.load.deleteCurrency = loadStatus.error;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrencies,
  setSelectedCurrency,
  unsetCurrencies,
  unsetSelectedCurrency,
} = currenciesSlice.actions;
export default currenciesSlice.reducer;
